import { useState, type FormEvent } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import Button from "@/components/base/Button";
import { Input } from "@/components/base/Field";
import { useToast, ToastProvider } from "@/components/base/Toast";
import { useAuth } from "@/contexts/AuthContext";
import { forgotPassword } from "@/services/authService";
import BrandMark from "@/components/base/BrandMark";

/** « Mot de passe oublié » : demande d'un lien de réinitialisation par e-mail. */
function ForgotPasswordPanel({ initialEmail, onBack }: { initialEmail: string; onBack: () => void }) {
  const { notify } = useToast();
  const [email, setEmail] = useState(initialEmail);
  const [sending, setSending] = useState(false);
  const [sentTo, setSentTo] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSending(true);
    try {
      await forgotPassword(email);
      setSentTo(email.trim());
    } catch (err) {
      notify(err instanceof Error ? err.message : "Envoi impossible.", "error");
    } finally {
      setSending(false);
    }
  };

  if (sentTo) {
    return (
      <div className="mt-8 space-y-4">
        <p className="flex items-start gap-2 rounded-md border border-primary-200 bg-primary-50 px-3 py-3 text-sm text-primary-800">
          <i className="ri-mail-send-line mt-0.5"></i>
          <span>
            Si un compte correspond à <strong>{sentTo}</strong>, un e-mail vient de lui être envoyé avec un lien
            pour choisir un nouveau mot de passe. Le lien est valable 1 heure.
          </span>
        </p>
        <p className="text-xs text-foreground-500">
          Rien reçu ? Vérifiez les courriers indésirables, ou demandez à l'administrateur de votre école de vous
          donner un mot de passe provisoire.
        </p>
        <Button variant="secondary" className="w-full" icon="ri-arrow-left-line" onClick={onBack}>
          Retour à la connexion
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="mt-8 space-y-4">
      <p className="text-sm text-foreground-600">
        Indiquez l'adresse e-mail de votre compte : nous vous enverrons un lien pour choisir un nouveau mot de passe.
      </p>
      <Input
        label="Adresse email"
        type="email"
        name="email"
        placeholder="vous@votre-ecole.gn"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        required
      />
      <Button type="submit" size="lg" className="w-full" icon="ri-mail-send-line" disabled={sending}>
        {sending ? "Envoi..." : "Recevoir le lien"}
      </Button>
      <button
        type="button"
        onClick={onBack}
        className="w-full text-center text-xs text-foreground-500 hover:text-foreground-800 cursor-pointer"
      >
        Retour à la connexion
      </button>
    </form>
  );
}

function LoginForm() {
  const navigate = useNavigate();
  const location = useLocation();
  const { notify } = useToast();
  const { login, user, loading: sessionLoading } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [remember, setRemember] = useState(true);
  const [loading, setLoading] = useState(false);
  const [forgot, setForgot] = useState(false);

  /** Page demandée avant la redirection vers la connexion. */
  const from = (location.state as { from?: string } | null)?.from ?? "/dashboard";
  /** Message transmis par la page précédente (ex. mot de passe changé). */
  const notice = (location.state as { notice?: string } | null)?.notice;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !password.trim()) {
      notify("Veuillez renseigner votre email et votre mot de passe.", "error");
      return;
    }
    setLoading(true);
    try {
      const account = await login(email, password, remember);
      notify(`Connexion réussie. Bienvenue ${account.firstName} !`);
      navigate(from, { replace: true });
    } catch (err) {
      notify(err instanceof Error ? err.message : "Connexion impossible.", "error");
    } finally {
      setLoading(false);
    }
  };

  if (!sessionLoading && user) return <Navigate to="/dashboard" replace />;

  return (
    <div className="grid min-h-screen grid-cols-1 lg:grid-cols-2">
      <div className="relative hidden overflow-hidden lg:block">
        <img
          src="/images/connexion-bibliotheque.jpg"
          alt="Des élèves lisent un livre ensemble dans une bibliothèque"
          className="absolute inset-0 h-full w-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/40 via-foreground-950/50 to-foreground-950/70"></div>
        <div className="relative flex h-full flex-col justify-between p-10 text-background-50">
          <div className="flex items-center gap-3">
            <BrandMark />
            <span className="text-sm font-semibold font-heading">WaitechEdu</span>
          </div>
          <div>
            <h2 className="text-3xl font-semibold font-heading leading-snug">
              Former les esprits,
              <br />
              éclairer l'avenir.
            </h2>
            <p className="mt-4 max-w-md text-sm leading-relaxed text-background-100/80">
              Une plateforme unique pour piloter les élèves, les inscriptions, le personnel et les
              finances des cycles Primaire et Secondaire.
            </p>
            <div className="mt-6 flex flex-wrap gap-2 text-xs">
              {["Élèves & inscriptions", "Classes & matières", "Finances par cycle"].map((t) => (
                <span key={t} className="rounded-full border border-background-50/30 px-3 py-1">
                  {t}
                </span>
              ))}
            </div>
          </div>
          <p className="text-xs text-background-100/60">
            WaitechEdu — gestion scolaire multi-école
          </p>
        </div>
      </div>

      <div className="flex items-center justify-center bg-background-50 px-6 py-12">
        <div className="w-full max-w-sm animate-slide-up">
          <Link to="/" className="mb-8 inline-flex items-center gap-2 text-sm text-foreground-500 hover:text-foreground-800 cursor-pointer">
            <i className="ri-arrow-left-line"></i>
            Retour à l'accueil
          </Link>
          <img
            src="/logo-waitechedu.png"
            alt="WaitechEdu — Le numérique au service du savoir"
            width={680}
            height={497}
            className="mb-6 h-24 w-auto"
          />
          <h1 className="text-2xl font-semibold font-heading text-foreground-950">
            {forgot ? "Mot de passe oublié" : "Connexion"}
          </h1>
          <p className="mt-1 text-sm text-foreground-500">
            {forgot ? "Recevez un lien de réinitialisation par e-mail." : "Accédez à l'espace de gestion de votre école."}
          </p>

          {forgot ? (
            <ForgotPasswordPanel initialEmail={email} onBack={() => setForgot(false)} />
          ) : (
          <>
          {notice && (
            <p className="mt-6 flex items-start gap-2 rounded-md border border-primary-200 bg-primary-50 px-3 py-2.5 text-xs text-primary-800">
              <i className="ri-checkbox-circle-line text-sm"></i>
              {notice}
            </p>
          )}

          <form onSubmit={handleSubmit} className="mt-8 space-y-4">
            <Input
              label="Adresse email"
              type="email"
              name="email"
              placeholder="vous@votre-ecole.gn"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            <div>
              <div className="mb-1.5 flex items-center justify-between">
                <span className="text-xs font-medium text-foreground-700">
                  Mot de passe <span className="text-accent-600">*</span>
                </span>
                <button
                  type="button"
                  onClick={() => setForgot(true)}
                  className="text-xs text-primary-700 hover:underline cursor-pointer"
                >
                  Mot de passe oublié ?
                </button>
              </div>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2 pr-10 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute inset-y-0 right-2 my-auto press w-9 h-9 sm:w-7 sm:h-7 flex items-center justify-center rounded-md text-foreground-400 hover:text-foreground-700 cursor-pointer"
                  aria-label={showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                >
                  <i className={showPassword ? "ri-eye-off-line" : "ri-eye-line"}></i>
                </button>
              </div>
            </div>

            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={remember}
                onChange={(e) => setRemember(e.target.checked)}
                className="h-4 w-4 rounded border-background-300 text-primary-600 focus:ring-primary-200"
              />
              <span className="text-xs text-foreground-600">Se souvenir de moi</span>
            </label>

            <Button type="submit" size="lg" className="w-full" icon="ri-login-box-line" disabled={loading}>
              {loading ? "Connexion..." : "Se connecter"}
            </Button>
          </form>
          </>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Login() {
  return (
    <ToastProvider>
      <LoginForm />
    </ToastProvider>
  );
}